<template>
    <IonApp :style="style">
        <IonRouterOutlet
            :animated="!mock.mocking()"
            :animation="navigationAnimation"
        />

        <DNotificationLayout
            v-if="snackbars.length > 0"
            class="dark d-snackbar-layout z-99999"
        >
            <Snackbar
                v-for="snackbar in snackbars"
                :key="snackbar.id"
                :snackbar="snackbar"
            />
        </DNotificationLayout>

        <div
            v-if="showLoadingOverlay"
            class="bg-white/50 fixed z-50 inset-0 flex items-center justify-center"
        >
            <UILoading class="w-12 h-12" />
        </div>
    </IonApp>
</template>

<script setup>
    import { onBeforeMount } from 'vue'
    import { Capacitor, ExceptionCode } from '@capacitor/core'
    import { SafeArea } from '@aashu-dubey/capacitor-statusbar-safe-area'
    import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
    import { isEmpty } from 'lodash'

    import { DNotificationLayout } from '@digistorm/spark'
    import { statusBar, mock } from '@/services'
    import { useLoadingOverlays } from '@/composables/loadingOverlays'
    import { useVersionStore } from '@/stores'
    import { DateHelper } from '@/support/DateHelper'
    import { provideDateHelper } from '@/composables/useDateHelper'

    const appStore = useAppStore()
    const deviceStore = useDeviceStore()
    const versionStore = useVersionStore()

    const { snackbars } = useSnackbars()
    const { navigationAnimation } = useAnimations()
    const { loadingOverlays } = useLoadingOverlays()

    const KEYBOARD_OPENING = 'opening'
    const KEYBOARD_OPEN = 'open'
    const KEYBOARD_CLOSING = 'closing'
    const KEYBOARD_CLOSED = 'closed'

    const { device, isAndroid, isIos } = storeToRefs(deviceStore)
    const { version } = storeToRefs(versionStore)
    const safeAreaTop = ref(0)

    const appHeight = ref('100vh')
    const keyboardState = ref(KEYBOARD_CLOSED)
    const keyboardModalOffset = ref(0)

    mock.initialise()

    // Keep a copy of the query / search string the app was launched with
    appStore.setInitialUrlSearch(window.location.search || '')

    const showLoadingOverlay = computed(() => loadingOverlays.length > 0)

    const safeAreaOverrides = computed(() => {
        const initialSearchParams = new URLSearchParams(appStore.initialUrlSearch)

        switch (initialSearchParams.get('emulate')) {
            case 'iPhone11':
                return {
                    top: '48px', bottom: '34px', left: '0', right: '0',
                }
            case 'iPhone14Pro':
                return {
                    top: '59px', bottom: '34px', left: '0', right: '0',
                }
            case 'iPad':
                return {
                    top: '20px', bottom: '0', left: '0', right: '0',
                }
            default:
                break
        }

        // On iOS and web the CSS safe-area variables are set correctly
        if (!device.value || !isAndroid.value) {
            return {}
        }

        // On android the environment variables for safe areas aren't set correctly so we need to adjust
        // When environment variables are setup correctly on android and windowOptOutEdgeToEdgeEnforcement is removed we should remove the zeroing
        // TODO: AP-2961 Remove this code
        const overrides = {
            top: '0', bottom: '0', left: '0', right: '0',
        }

        // Since we overlay the statusbar we need to add that to the top area
        // TODO: AP-2961 Remove this code
        if (safeAreaTop.value) {
            overrides.top = `${safeAreaTop.value}px`
        }

        return overrides
    })

    const style = computed(() => {
        const overrides = safeAreaOverrides.value

        return {
            '--ds-modal-keyboard-offset': `${keyboardModalOffset.value}px`,
            height: appHeight.value,
            ...(overrides.top ? {
                '--ion-safe-area-top': overrides.top,
                '--beyond-safe-area-inset-top': overrides.top,
            } : {}),
            ...(overrides.bottom ? {
                '--ion-safe-area-bottom': overrides.bottom,
                '--beyond-safe-area-inset-bottom': overrides.bottom,
            } : {}),
            ...(overrides.left ? {
                '--ion-safe-area-left': overrides.left,
                '--beyond-safe-area-inset-left': overrides.left,
            } : {}),
            ...(overrides.right ? {
                '--ion-safe-area-right': overrides.right,
                '--beyond-safe-area-inset-right': overrides.right,
            } : {}),
        }
    })

    const updateKeyboardState = (state, height) => {
        keyboardState.value = state

        // Android resizes the webview so we don't need to adjust for the keyboard height
        // We choose to on iOS only for better control over the experience
        if (!isIos.value) {
            appHeight.value = '100vh'
            keyboardModalOffset.value = 0
            return
        }

        keyboardModalOffset.value = keyboardState.value === KEYBOARD_OPENING
            ? height
            : 0

        // This behaviour replicates the KeyboardResize.Ionic behaviour from the keyboard plugin, but gives us more control
        // It allows us to ensure the height and modal offsets are in sync, otherwise there are visual glitches
        appHeight.value = keyboardState.value === KEYBOARD_OPEN
            ? `calc(100vh - ${height}px)`
            : '100vh'
    }

    if (Capacitor.isNativePlatform()) {
        statusBar.setOverlaysWebView(true)

        // Configure the keyboard to not resize the webview
        Keyboard.setResizeMode({ mode: KeyboardResize.None })
            .catch((err) => {
                // Ignore unimplemented error, we expect this for Android
                if (Error.isError(err) && err.code === ExceptionCode.Unimplemented) {
                    return
                }
                throw err
            })

        Keyboard.addListener('keyboardWillShow', (e) => {
            updateKeyboardState(KEYBOARD_OPENING, e.keyboardHeight)
        })

        Keyboard.addListener('keyboardDidShow', (e) => {
            updateKeyboardState(KEYBOARD_OPEN, e.keyboardHeight)
        })

        Keyboard.addListener('keyboardWillHide', () => {
            updateKeyboardState(KEYBOARD_CLOSING, 0)
        })

        Keyboard.addListener('keyboardDidHide', () => {
            updateKeyboardState(KEYBOARD_CLOSED, 0)
        })

        SafeArea.getStatusBarHeight().then(({ height }) => {
            safeAreaTop.value = Math.max(safeAreaTop.value, height)
            console.log('status bar height', height)
        })

        SafeArea.getSafeAreaInsets().then((insets) => {
            safeAreaTop.value = Math.max(safeAreaTop.value, insets.top)
            console.log('insets', insets)
        })
    }

    const dateHelper = computed(() => {
        if (isEmpty(version.value)) {
            return
        }

        const deviceTimezone = mock.screenshotting()
            ? version.value.school_timezone
            : Intl.DateTimeFormat().resolvedOptions().timeZone

        return new DateHelper(version.value.school_locale, version.value.school_timezone, deviceTimezone)
    })

    provideDateHelper(dateHelper)

    onBeforeMount(() => {
        const initialSearchParams = new URLSearchParams(appStore.initialUrlSearch)

        switch (initialSearchParams.get('emulate')) {
            case 'iPad':
                deviceStore.forceTabletMode = true
                break
            default:
                break
        }
    })
</script>
